import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import DOMPurify from 'dompurify';
import { useBiDataFormatter } from '~/bi/components/widgets/table-widget/composables/bi-table-data-formatter.composable.js';

dayjs.extend(customParseFormat);
export function CustomEditors() {
  function registerMultiSelect() {
    const { formatArrayAsTags } = useBiDataFormatter();
    const Handsontable = window.BiHandsontable;

    class MultiSelectEditor extends Handsontable.editors.BaseEditor {
      constructor(instance) {
        super(instance);
        this.openOnMouseDown = true;
        this.selectedValues = [];
        this.handleScroll = this.handleScroll.bind(this); // Bind scroll handler
      }

      init() {
        // Create the wrapper div
        this.wrapper = document.createElement('div');
        this.wrapper.classList.add('htDropdownWrapper', 'max-h-[200px]', 'scrollbar');
        this.wrapper.style.position = 'absolute';
        this.wrapper.style.zIndex = '100';
        this.wrapper.style.border = '1px solid #ccc';
        this.wrapper.style.backgroundColor = '#fff';
        this.wrapper.style.padding = '10px';
        this.wrapper.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.1)';
        this.wrapper.style.borderRadius = '4px';
        this.wrapper.style.textAlign = 'left';
        this.wrapper.style.display = 'none'; // Initially hide the dropdown

        // Create search input
        this.searchInput = document.createElement('input');
        this.searchInput.type = 'text';
        this.searchInput.classList.add('multi-select-search', 'outline-none', 'flex-1', 'min-w-[60px]', 'text-sm');

        // Handle search input
        this.current_highlighted_option = 0;
        this.searchInput.addEventListener('input', () => {
          this.current_highlighted_option = 0;
          this.renderOptions();
        });
        this.searchInput.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.current_highlighted_option++;
            this.renderOptions();
          }
          if (event.key === 'ArrowUp') {
            event.preventDefault();
            if (this.current_highlighted_option === 0)
              return;
            this.current_highlighted_option--;
            this.renderOptions();
          }
          if (event.key === 'Backspace' && this.searchInput.value === '') {
            this.selectedValues.pop();
            this.updateTickIconState();
            this.updateTableCell();
          }
          if (event.key === 'Enter') {
            const highlightedOption = this.selectBox.querySelector('.highlighted-option');
            if (highlightedOption) {
              const label = highlightedOption.querySelector('label');
              const optionValue = label.getAttribute('for');
              if (!this.selectedValues.includes(optionValue)) {
                this.selectedValues.push(optionValue);
              }
              const is_new_option = label.getAttribute('data-is-new-option');
              if (is_new_option) {
                this.dropdown_options.push(optionValue);
                this.searchInput.value = '';
              }
              this.updateTickIconState();
              this.updateTableCell();
              this.renderOptions();
            }
          }
        });

        // Create the select box that contains options
        this.selectBox = document.createElement('div');
        this.selectBox.classList.add('multi-select-dropdown');

        this.wrapper.appendChild(this.selectBox);
        this.hot.rootElement.appendChild(this.wrapper); // Append wrapper to the root element

        // Listen for scroll events
        this.hot.addHook('afterScrollHorizontally', this.handleScroll);
        this.hot.addHook('afterScrollVertically', this.handleScroll);

        // Optionally handle window scrolling
        window.addEventListener('scroll', this.handleScroll, true);
      }

      prepare(row, col, prop, td, originalValue, cellProperties) {
        if (this.searchInput) {
          this.searchInput.value = '';
        }

        super.prepare(row, col, prop, td, originalValue, cellProperties);
        this.TD = td;

        if (Array.isArray(originalValue)) {
          this.selectedValues = originalValue;
        }
        else if (typeof originalValue === 'string' && originalValue.length) {
          this.selectedValues = originalValue.split(',');
        }
        else {
          this.selectedValues = [];
        }

        this.dropdown_options = cellProperties.options || [];
        this.renderOptions();
      }

      renderOptions() {
        // Clear the selectBox content
        this.selectBox.innerHTML = '';
        let options = this.dropdown_options;
        const searchValue = this.searchInput.value;
        if (searchValue?.length) {
          options = options.filter(option => option.toLowerCase().includes(searchValue.toLowerCase()));
        }

        const highlightMatch = (text, query) => {
          if (!query)
            return text;
          const escapedQuery = query.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'); // escape special chars
          const regex = new RegExp(`(${escapedQuery})`, 'gi');
          return text.replace(regex, '<mark>$1</mark>');
        };

        let has_exact_match = false;
        if (searchValue?.length) {
          has_exact_match = options.some(option => option.toLowerCase() === searchValue.toLowerCase());
        }

        if (!has_exact_match && searchValue.length) {
          options = [searchValue, ...options];
        }

        if (this.current_highlighted_option >= options.length) {
          this.current_highlighted_option = options.length - 1;
        }
        else if (this.current_highlighted_option < 0) {
          this.current_highlighted_option = 0;
        }
        else {
          this.current_highlighted_option ??= 0;
        }

        options.forEach((option, index) => {
          const optionContainer = document.createElement('div');
          optionContainer.classList.add(
            'multi-select-option',
            'flex',
            'justify-between',
            'items-center',
            'px-2.5',
            'py-1.5',
            'cursor-pointer',
            'hover:bg-gray-100',
            'transition-colors',
            'duration-150',
            'rounded',
          );

          if (index === this.current_highlighted_option) {
            optionContainer.classList.add('highlighted-option', 'bg-primary-50');
          }
          else {
            optionContainer.classList.remove('highlighted-option', 'bg-primary-50');
          }

          const label = document.createElement('label');
          if (index === 0 && !has_exact_match && searchValue.length) {
            label.innerHTML = DOMPurify.sanitize(`+ Add "${searchValue}"`);
            // Add a custom attribute (data-is-new-option)
            label.setAttribute('data-is-new-option', 'true');
          }
          else {
            label.innerHTML = DOMPurify.sanitize(highlightMatch(option, searchValue));
          }
          label.title = option;
          label.style.textOverflow = 'ellipsis';
          label.style.overflow = 'hidden';
          label.style.whiteSpace = 'nowrap';
          label.style.cursor = 'pointer';
          label.style.fontSize = '14px';
          label.setAttribute('for', option);

          const tickIcon = document.createElement('span');
          tickIcon.style.marginLeft = '10px';
          tickIcon.style.display = 'inline-block';
          tickIcon.style.visibility = this.selectedValues.includes(option) ? 'visible' : 'hidden';
          tickIcon.style.color = '#1570EF';

          // Add SVG icon inside the span
          tickIcon.innerHTML = `
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          `;

          optionContainer.appendChild(label);
          optionContainer.appendChild(tickIcon);
          this.selectBox.appendChild(optionContainer);

          optionContainer.addEventListener('click', () => {
            const index = this.selectedValues.indexOf(option);
            if (index === -1) {
              this.selectedValues.push(option);
            }
            else {
              this.selectedValues.splice(index, 1);
            }

            this.updateTickIconState();
            this.updateTableCell();
          });
        });

        if (options.length) {
          this.handleScrollToHighlightedOption();
        }
      }

      handleScrollToHighlightedOption() {
        const highlightedElement = this.selectBox.querySelector('.highlighted-option');
        if (!highlightedElement)
          return;
        highlightedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        });
      }

      getValue() {
        return this.selectedValues.filter(value => !!value?.length).join(', ');
      }

      setValue(value) {
        const parsedValue = Array.isArray(value)
          ? value
          : (typeof value === 'string' ? value.split(', ').map(v => v.trim()).filter(v => v.length) : []);
        this.selectedValues = parsedValue;

        this.updateTickIconState();
      }

      updateTickIconState() {
        const optionContainers = this.selectBox.querySelectorAll('div');
        optionContainers.forEach((optionContainer) => {
          const tickIcon = optionContainer.querySelector('span');
          const label = optionContainer.querySelector('label');
          const optionValue = label.textContent;

          tickIcon.style.visibility = this.selectedValues.includes(optionValue) ? 'visible' : 'hidden';
        });
      }

      updateTableCell() {
        this.TD.innerHTML = ''; // Clear the cell

        const selectedValues = this.selectedValues || [];

        // Container (flex layout)
        const div_container = document.createElement('div');

        div_container.classList.add(
          'flex',
          'flex-wrap',
          'gap-[2px]',
          'items-center',
        );
        div_container.style.cursor = 'text';

        // Loop through selected values and create tag elements
        selectedValues.forEach((value) => {
          const tag = document.createElement('div');
          tag.classList.add(
            'inline-block',
            'px-2',
            'py-[2px]',
            'rounded-[12px]',
            'text-[11px]',
            'font-medium',
            'whitespace-nowrap',
            'bg-gray-100',
          );
          tag.textContent = value;
          div_container.appendChild(tag);
        });

        div_container.appendChild(this.searchInput);

        // Add container to the TD and focus the input
        this.TD.appendChild(div_container);
        this.searchInput.focus();

        const container = this.hot.rootElement.querySelector('.ht_master .wtHolder');
        if (container) {
          if (!this.div_container_height) {
            container.scrollTop += 5;
          }
          else if (this.div_container_height > div_container.offsetHeight) {
            container.scrollTop -= 5;
          }
          else if (this.div_container_height < div_container.offsetHeight) {
            container.scrollTop += 5;
          }
        }
        this.div_container_height = div_container.offsetHeight;
        this.handleScroll();
        setTimeout(() => {
          this.handleScroll();
        }, 500);
      }

      open() {
        if (!this.wrapper)
          return;
        const cellRect = this.getEditedCellRect();

        if (cellRect) {
          const { top, start, width, height } = cellRect;

          // Temporarily display the dropdown off-screen to measure its full height.
          this.wrapper.style.visibility = 'hidden';
          this.wrapper.style.display = ''; // Ensure dropdown is rendered
          this.wrapper.style.top = `-9999px`; // Move it off-screen
          this.wrapper.style.left = `-9999px`;

          // Use setTimeout to ensure the browser has time to render the dropdown and measure it properly.
          setTimeout(() => {
            const dropdownHeight = this.wrapper.offsetHeight + 50; // Add buffer space
            this.wrapper.style.display = 'none'; // Hide it again after measuring
            this.wrapper.style.visibility = ''; // Reset visibility

            // Get the table container's bounding box and viewport dimensions.
            const tableContainerRect = this.hot.view._wt.wtTable.holder.getBoundingClientRect();
            const tableBottom = tableContainerRect.bottom;
            const tableTop = tableContainerRect.top;

            const viewportHeight = window.innerHeight;

            // Calculate space above and below the cell.
            const spaceBelow = Math.min(tableBottom, viewportHeight) - (top + height);
            const spaceAbove = top - Math.max(0, tableTop);

            if (spaceBelow >= dropdownHeight) {
              // Enough space below the cell.
              this.wrapper.style.top = `${top + height}px`; // Adjust to reduce the gap
            }
            else if (spaceAbove >= dropdownHeight) {
              // Enough space above the cell.
              this.wrapper.style.top = `${top - dropdownHeight + 50}px`; // Adjust to reduce the gap
            }
            else {
              // Not enough space in either direction. Adjust to fit in available space.
              if (spaceBelow > spaceAbove) {
                // More space below than above, restrict height to fit below.
                this.wrapper.style.top = `${top + height}px`;
                this.wrapper.style.maxHeight = `${spaceBelow}px`;
                this.wrapper.style.overflowY = 'auto'; // Enable scrolling
              }
              else {
                // More space above than below, restrict height to fit above.
                this.wrapper.style.top = `${top - spaceAbove}px`;
                this.wrapper.style.maxHeight = `${spaceAbove}px`;
                this.wrapper.style.overflowY = 'auto'; // Enable scrolling
              }
            }

            // Apply the necessary positioning and display the dropdown.
            this.wrapper.style.left = `${start}px`;
            this.wrapper.style.width = `${width - 22}px`;
            this.wrapper.style.display = ''; // Now display it fully

            // Add scroll event listeners to ensure the dropdown stays positioned.
            this.hot.addHook('afterScrollHorizontally', this.handleScroll);
            this.hot.addHook('afterScrollVertically', this.handleScroll);
          }, 200); // Timeout ensures the browser renders the dropdown before measuring.
        }
        this.updateTableCell();
      }

      handleScroll() {
        if (this.wrapper && this.wrapper.style.display !== 'none') {
          const cellRect = this.getEditedCellRect();

          if (cellRect) {
            const { top, start, width, height } = cellRect;

            // Keep dropdown positioned during scroll events.
            const dropdownHeight = this.wrapper.offsetHeight + 20; // Include buffer space

            const tableContainerRect = this.hot.view._wt.wtTable.holder.getBoundingClientRect();
            const tableBottom = tableContainerRect.bottom;
            const tableTop = tableContainerRect.top;

            const viewportHeight = window.innerHeight;

            // Calculate space above and below again during scroll.
            const spaceBelow = Math.min(tableBottom, viewportHeight) - (top + height);
            const spaceAbove = top - Math.max(0, tableTop);

            if (spaceBelow >= dropdownHeight) {
              // Enough space below, keep it below.
              this.wrapper.style.top = `${top + height}px`;
            }
            else if (spaceAbove >= dropdownHeight) {
              // Enough space above, keep it above.
              this.wrapper.style.top = `${top - dropdownHeight}px`;
            }
            else if (spaceBelow > spaceAbove) {
              // More space below, restrict height to fit below.
              this.wrapper.style.top = `${top + height}px`;
              this.wrapper.style.maxHeight = `${spaceBelow}px`;
              this.wrapper.style.overflowY = 'auto';
            }
            else {
              // More space above, restrict height to fit above.
              this.wrapper.style.top = `${top - spaceAbove}px`;
              this.wrapper.style.maxHeight = `${spaceAbove}px`;
              this.wrapper.style.overflowY = 'auto';
            }

            this.wrapper.style.left = `${start}px`;
            this.wrapper.style.width = `${width - 22}px`;
          }
        }
      }

      close() {
        if (!this.wrapper)
          return;
        this.wrapper.style.display = 'none';
        if (this.searchInput) {
          this.searchInput.value = '';
        }
        this.cellProperties.handleUpdateOptions(this.dropdown_options);

        // Remove scroll event listeners
        this.hot.removeHook('afterScrollHorizontally', this.handleScroll);
        this.hot.removeHook('afterScrollVertically', this.handleScroll);
        window.removeEventListener('scroll', this.handleScroll, true);
      }

      focus() {
      }
    }

    function multiSelectRenderer(instance, td, row, col, prop, value) {
      if (window.BiHandsontable) {
        // eslint-disable-next-line prefer-rest-params
        window.BiHandsontable.renderers.TextRenderer.apply(this, arguments);
      }

      td.innerHTML = '';

      const formatted_value = Array.isArray(value) ? value : (value || '').split(', ').map(v => v.trim()).filter(v => v.length);
      const tags_container = formatArrayAsTags(formatted_value, '#F2F4F7');
      td.appendChild(tags_container);
      return td;
    }

    // Register globally
    Handsontable.editors.registerEditor('multiselect', MultiSelectEditor);
    Handsontable.renderers.registerRenderer('multiSelectRenderer', multiSelectRenderer);
  }

  function dateHasTime(input) {
    return /\d{1,2}:\d{2}/.test(input); // looks for hh:mm
  }

  function parseDateTimeSegments(date_value) {
    // Default fallback value
    const fallback = dayjs('01/01/2025 12:00 AM', 'DD/MM/YYYY hh:mm A');

    // Supported input formats
    const INPUT_FORMATS = [
      // Default formats
      'DD/MM/YYYY hh:mm A',
      'DD/MM/YYYY',
      'YYYY-MM-DD hh:mm A',
      'YYYY-MM-DD',

      // Additional formats
      'DD/MM/YYYY HH:mm',
      'YYYY-MM-DD HH:mm',
      'YYYY/MM/DD',
      'YYYY/MM/DD HH:mm',
      'DD-MM-YYYY',
      'DD-MM-YYYY HH:mm',
      'DD-MMM-YYYY',
      'DD-MMM-YYYY HH:mm',
      'MM/DD/YYYY',
      'MM/DD/YYYY HH:mm',

      // Add day-month-abbrev-year formats (e.g. "27 Jun 2023", "7 Jun 2023")
      'DD MMM YYYY',
      'D MMM YYYY',
      'DD MMM YYYY HH:mm',
      'D MMM YYYY HH:mm',
      'DD MMM YYYY hh:mm A',
      'D MMM YYYY hh:mm A',

      // ISO 8601
      'YYYY-MM-DDTHH:mm',
      'YYYY-MM-DDTHH:mm:ss',
      'YYYY-MM-DDTHH:mm:ssZ',
      'YYYY-MM-DDTHH:mm:ss.SSSZ',
      'YYYY-MM-DDTHH:mm:ss.SSS[Z]',
    ];

    let parsed = null;

    // Attempt parsing with all supported formats
    for (const fmt of INPUT_FORMATS) {
      const attempt = dayjs(date_value, fmt, true); // strict mode
      if (attempt.isValid()) {
        parsed = attempt;
        break;
      }
    }

    // If none matched, use fallback
    if (!parsed)
      parsed = fallback;

    // If the input had no time, force 12:00 AM
    if (!dateHasTime(date_value)) {
      parsed = parsed.hour(0).minute(0).second(0);
    }

    // Convert to uniform display format
    const formatted = parsed.format('DD/MM/YYYY hh:mm A');

    // Break into segments
    const [datePart, timePart, ampm] = formatted.split(' ');
    const [day, month, year] = datePart.split('/');
    const [hour, minute] = timePart.split(':');

    return {
      day,
      month,
      year,
      hour,
      minute,
      ampm,
      actual_date: parsed,
    };
  }

  function registerDateTime() {
    const Handsontable = window.BiHandsontable;

    const limits = {
      day: [1, 31],
      month: [1, 12],
      year: [1, 9999],
      hour: [1, 12], // Changed to 12-hour format
      minute: [0, 59],
    };

    function clamp(val, min, max) {
      val = Number.parseInt(val || '0');
      return Number.isNaN(val) ? min : Math.min(max, Math.max(min, val));
    }

    class DateTimeEditor extends Handsontable.editors.BaseEditor {
      constructor(instance) {
        super(instance);
        this.inputs = {};
        this.editorDiv = null;
        this.listenersAttached = false;
        this.currentDataType = null;
      }

      init() {
        this.createElements();
      }

      prepare(row, col, prop, td, originalValue, cellProperties) {
        super.prepare(row, col, prop, td, originalValue, cellProperties);
        this.TD = td;
        this.originalValue = originalValue;

        // Check if data type has changed and force recreation
        if (this.currentDataType !== cellProperties?.data_type) {
          this.destroyElements();
          this.currentDataType = cellProperties?.data_type;
        }

        // Ensure elements are created
        this.createElements();
      }

      destroyElements() {
        if (this.editorDiv && this.editorDiv.parentNode) {
          this.editorDiv.parentNode.removeChild(this.editorDiv);
        }
        this.editorDiv = null;
        this.inputs = {};
        this.listenersAttached = false;
      }

      createElements() {
        if (this.editorDiv) {
          return; // Already created
        }

        if (!this.hot || !this.hot.rootElement) {
          return; // Not ready yet
        }

        // Ensure inputs object exists
        if (!this.inputs) {
          this.inputs = {};
        }

        // Create the wrapper div for the editor
        this.editorDiv = document.createElement('div');
        this.editorDiv.className = 'htCustomEditor';
        this.editorDiv.style.display = 'none';
        this.editorDiv.style.gap = '2px';
        this.editorDiv.style.fontFamily = 'sans-serif';
        this.editorDiv.style.fontSize = '14px';
        this.editorDiv.style.alignItems = 'center';
        this.editorDiv.style.height = '100%';
        this.editorDiv.style.padding = '0px';

        // Prevent finishing edit when clicking on the editor
        this.editorDiv.addEventListener('mousedown', (e) => {
          e.stopPropagation();
        });

        const parts = [
          { id: 'day', max: 2 },
          { sep: '/' },
          { id: 'month', max: 2 },
          { sep: '/' },
          { id: 'year', max: 4, cls: 'year' },
        ];

        // Only add time fields for timestamp data type
        if (this.cellProperties?.data_type === 'timestamp') {
          parts.push(
            { sep: ' ' },
            { id: 'hour', max: 2 },
            { sep: ':' },
            { id: 'minute', max: 2 },
            { sep: ' ' },
            { id: 'ampm', max: 2, readonly: true },
          );
        }

        parts.forEach((p) => {
          if (p.sep) {
            const span = document.createElement('span');
            span.className = 'sep';
            span.textContent = p.sep;
            span.style.userSelect = 'none';
            span.style.opacity = '0.7';
            this.editorDiv.appendChild(span);
          }
          else {
            const input = document.createElement('input');
            input.maxLength = p.max;
            input.id = p.id;
            input.style.width = p.id === 'year' ? '4ch' : (p.id === 'ampm' ? '3ch' : '2ch');
            input.style.textAlign = 'center';
            input.style.border = 'none';
            input.style.background = 'transparent';
            input.style.outline = 'none';
            input.style.fontSize = '14px';
            input.style.padding = '0';
            input.style.height = '24px';
            if (p.readonly) {
              input.readOnly = true;
              input.style.cursor = 'pointer';
            }
            if (p.cls)
              input.className = p.cls;
            this.inputs[p.id] = input;
            this.editorDiv.appendChild(input);
          }
        });

        this.hot.rootElement.appendChild(this.editorDiv);
      }

      focusInput(input) {
        input.focus();
        input.select();
      }

      open() {
        // Ensure elements are created
        this.createElements();

        if (!this.editorDiv) {
          return;
        }

        // Clear the TD before rendering editor
        this.TD.innerHTML = '';
        this.editorDiv.style.display = 'flex';
        this.TD.appendChild(this.editorDiv);

        this.handleSetInput(this.originalValue);

        // Setup event listeners only once
        if (!this.listenersAttached) {
          const inputArr = Object.entries(this.inputs);

          inputArr.forEach(([id, input], idx) => {
            input.autocomplete = 'off';
            input.addEventListener('keydown', (e) => {
              const caret = input.selectionStart;
              const len = input.value.length;

              if (id === 'ampm') {
                // Only allow arrow keys for AM/PM
                if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  input.value = input.value === 'AM' ? 'PM' : 'AM';
                  return;
                }
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  input.value = input.value === 'PM' ? 'AM' : 'PM';
                  return;
                }
                // Allow navigation keys
                if (e.key === 'ArrowLeft') {
                  e.preventDefault();
                  this.focusInput(this.inputs[inputArr[idx - 1][0]]);
                  return;
                }
                if (e.key === 'Enter') {
                  e.preventDefault();
                  this.finishEditing();
                  return;
                }
                if (e.key === 'Tab' || e.shiftKey) {
                  e.preventDefault();
                  if (e.key === 'Tab' && e.shiftKey) {
                    if (idx !== 0) {
                      this.focusInput(this.inputs[inputArr[idx - 1][0]]);
                    }
                  }
                  else if (idx < inputArr.length - 1) {
                    this.focusInput(this.inputs[inputArr[idx + 1][0]]);
                  }
                  return;
                }

                // Block all other keys for AM/PM
                e.preventDefault();
                return;
              }

              // Only access limits for non-ampm fields
              if (!limits[id]) {
                return;
              }

              const [min, max] = limits[id];
              const value = Number.parseInt(input.value || '0');

              if (e.key === 'ArrowUp') {
                e.preventDefault();
                input.value = clamp(value + 1, min, max).toString().padStart(id === 'year' ? 4 : 2, '0');
                return;
              }
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                input.value = clamp(value - 1, min, max).toString().padStart(id === 'year' ? 4 : 2, '0');
                return;
              }
              if (e.key === 'ArrowLeft' && caret === 0 && idx > 0) {
                e.preventDefault();
                this.focusInput(this.inputs[inputArr[idx - 1][0]]);
                return;
              }
              if (e.key === 'ArrowRight' && caret === len && idx < inputArr.length - 1) {
                e.preventDefault();
                this.focusInput(this.inputs[inputArr[idx + 1][0]]);
                return;
              }
              if (e.key === 'Enter') {
                e.preventDefault();
                this.finishEditing();
              }
              if (e.key === 'Tab' || e.shiftKey) {
                e.preventDefault();
                if (e.key === 'Tab' && e.shiftKey) {
                  if (idx !== 0) {
                    this.focusInput(this.inputs[inputArr[idx - 1][0]]);
                  }
                }
                else if (e.key === 'Tab') {
                  if (idx < inputArr.length - 1) {
                    this.focusInput(this.inputs[inputArr[idx + 1][0]]);
                  }
                }
              }
            });

            input.addEventListener('input', () => {
              input.value = input.value.replace(/\D/g, '');

              // Try smart auto-advance first
              const didAutoAdvance = this.handleSmartAutoAdvance(input, id, idx, inputArr);

              // Original max length check (fallback) - only if smart advance didn't trigger
              if (!didAutoAdvance && input.value.length === input.maxLength) {
                if (idx < inputArr.length - 1) {
                  this.focusInput(this.inputs[inputArr[idx + 1][0]]);
                }
              }
            });

            input.addEventListener('click', (e) => {
              e.stopPropagation();
              this.focusInput(input);
            });

            input.addEventListener('focus', () => {
              // Add highlight styling when input is focused
              input.style.backgroundColor = '#000000';
              input.style.color = '#fff';
              input.style.border = '1px solid #fff';
              input.style.borderRadius = '4px';
              input.style.boxShadow = '0px 1px 2px rgba(16, 24, 40, 0.05), 0px 0px 0px 2px #fff';
              input.style.padding = '0px 4px';
            });

            input.addEventListener('blur', () => {
              // Remove highlight styling and validate value
              input.style.backgroundColor = 'transparent';
              input.style.color = '#000';
              input.style.border = 'none';
              input.style.borderRadius = '';
              input.style.boxShadow = '';
              input.style.padding = '0px';

              if (!limits[id]) {
                return;
              }

              const [min, max] = limits[id];
              const value = clamp(Number.parseInt(input.value || '0'), min, max);
              input.value = value.toString().padStart(id === 'year' ? 4 : 2, '0');
            });
          });

          this.listenersAttached = true;
        }

        setTimeout(() => {
          if (this.inputs.day) {
            this.focusInput(this.inputs.day);
          }
        }, 0);
      }

      close() {
        if (this.editorDiv && this.editorDiv.parentNode) {
          this.editorDiv.style.display = 'none';
        }
      }

      getValue() {
        if (!this.inputs.day)
          return this.originalValue;

        const day = (this.inputs.day.value || '01').padStart(2, '0');
        const month = (this.inputs.month.value || '01').padStart(2, '0');
        const year = (this.inputs.year.value || '2025').padStart(4, '0');

        if (this.cellProperties?.data_type === 'timestamp' && this.inputs.hour) {
          let hour = Number.parseInt(this.inputs.hour.value || '12');
          const minute = (this.inputs.minute.value || '00').padStart(2, '0');
          const ampm = this.inputs.ampm.value || 'AM';

          // Convert 12-hour to 24-hour format
          if (ampm === 'PM' && hour !== 12) {
            hour += 12;
          }
          else if (ampm === 'AM' && hour === 12) {
            hour = 0;
          }

          const dateString = `${year}-${month}-${day}T${hour.toString().padStart(2, '0')}:${minute}:00`;
          return dayjs(dateString).format('YYYY-MM-DD hh:mm A');
        }

        return `${year}-${month}-${day}`;
      }

      setValue(val) {
        if (!val)
          return;

        // Ensure elements are created
        this.createElements();

        if (!this.inputs.day || !this.inputs.month || !this.inputs.year)
          return;
        this.handleSetInput(val);
      }

      handleSetInput(val) {
        const { day, month, year, hour, minute, ampm } = parseDateTimeSegments(val);

        this.inputs.day.value = day || '01';
        this.inputs.month.value = month || '01';
        this.inputs.year.value = year || '2025';
        if (this.inputs.hour) {
          this.inputs.hour.value = hour || '00';
          this.inputs.minute.value = minute || '00';
          this.inputs.ampm.value = ampm || 'AM';
        }
      }

      handleSmartAutoAdvance(input, id, idx, inputArr) {
        if (input.value.length === 0 || id === 'ampm') {
          return false;
        }

        const currentValue = Number.parseInt(input.value);
        const [min, max] = limits[id] || [0, 99];
        let shouldAutoAdvance = false;

        if (id === 'day') {
          // Day: auto-advance if typed number can't form valid larger number
          // 4-9 → auto-advance (no valid days 40-99)
          // 1-3 → don't auto-advance (10-31 possible)
          if (input.value.length === 1) {
            shouldAutoAdvance = currentValue >= 4;
          }
          else if (input.value.length === 2) {
            shouldAutoAdvance = true;
          }
        }
        else if (id === 'month') {
          // Month: auto-advance if typed number can't form valid larger number
          // 2-9 → auto-advance (no valid months 20-99)
          // 1 → don't auto-advance (10-12 possible)
          if (input.value.length === 1) {
            shouldAutoAdvance = currentValue >= 2;
          }
          else if (input.value.length === 2) {
            shouldAutoAdvance = true;
          }
        }
        else if (id === 'hour') {
          // Hour (12-hour format): auto-advance if typed number can't form valid larger number
          // 2-9 → auto-advance (no valid hours 20-99 in 12-hour)
          // 1 → don't auto-advance (10-12 possible)
          if (input.value.length === 1) {
            shouldAutoAdvance = currentValue >= 2;
          }
          else if (input.value.length === 2) {
            shouldAutoAdvance = true;
          }
        }
        else if (id === 'minute') {
          // Minute: auto-advance if typed number can't form valid larger number
          // 6-9 → auto-advance (no valid minutes 60-99)
          // 0-5 → don't auto-advance (00-59 possible)
          if (input.value.length === 1) {
            shouldAutoAdvance = currentValue >= 6;
          }
          else if (input.value.length === 2) {
            shouldAutoAdvance = true;
          }
        }
        else if (id === 'year') {
          // Year: only auto-advance when 4 digits are entered
          shouldAutoAdvance = input.value.length === 4;
        }

        // Auto-advance to next field if conditions are met
        if (shouldAutoAdvance && idx < inputArr.length - 1) {
          // Validate and clamp the current value before advancing
          const clampedValue = clamp(currentValue, min, max);
          input.value = clampedValue.toString().padStart(id === 'year' ? 4 : 2, '0');

          this.focusInput(this.inputs[inputArr[idx + 1][0]]);
          return true;
        }

        return false;
      }

      focus() {
        if (this.inputs.day) {
          this.focusInput(this.inputs.day);
        }
      }
    }

    function dateTimeRenderer(instance, td, row, col, prop, value, cellProperties) {
      if (Handsontable) {
        // eslint-disable-next-line prefer-rest-params
        Handsontable.renderers.TextRenderer.apply(this, arguments);
      }

      // Clear the cell completely
      td.innerHTML = '';
      td.textContent = '';

      if (!value) {
        return td;
      }

      const { day, month, year, hour, minute, ampm, actual_date } = parseDateTimeSegments(value);
      if (!actual_date.isValid()) {
        return td;
      }

      // Create a container div to avoid conflicts
      const container = document.createElement('div');
      container.style.display = 'flex';
      container.style.alignItems = 'center';
      container.style.gap = '2px';
      container.style.height = '100%';
      container.style.fontFamily = 'sans-serif';
      container.style.fontSize = '14px';

      // Create spans for each part to match editor spacing
      const parts = [
        { text: day, width: '2ch' },
        { text: '/', width: 'auto' },
        { text: month, width: '2ch' },
        { text: '/', width: 'auto' },
        { text: year, width: '4ch' },
      ];
      if (cellProperties.data_type === 'timestamp') {
        parts.push(
          { text: ' ', width: 'auto' },
          { text: hour, width: '2ch' },
          { text: ':', width: 'auto' },
          { text: minute, width: '2ch' },
          { text: ' ', width: 'auto' },
          { text: ampm, width: '3ch' },
        );
      }
      parts.forEach((part) => {
        const span = document.createElement('span');
        span.textContent = part.text;
        span.style.width = part.width;
        span.style.minWidth = part.width;
        span.style.textAlign = 'center';
        span.style.display = 'inline-block';
        container.appendChild(span);
      });

      td.appendChild(container);

      return td;
    }

    // Register globally
    Handsontable.editors.registerEditor('datetime', DateTimeEditor);
    Handsontable.renderers.registerRenderer('datetimeRenderer', dateTimeRenderer);
  }

  return {
    registerMultiSelect,
    registerDateTime,
    parseDateTimeSegments,
  };
}
